/**
 * useAuth Hook
 * ============
 * Provides auth state and actions to all components.
 * Uses Zustand for global state — no prop drilling.
 *
 * Usage:
 *   const { user, isLoading, signInWithGoogle } = useAuth();
 */

import { useEffect } from "react";
import { create }    from "zustand";
import type { User } from "firebase/auth";
import { subscribeToAuthState, handleRedirectResult } from "@/features/auth/authService";

// ─── STORE ────────────────────────────────────────────────────────────────────
interface AuthState {
  user:          User | null;
  isLoading:     boolean;
  isVerified:    boolean;
  redirectError: string | null;

  _setUser:          (user: User | null) => void;
  _setLoading:       (loading: boolean)  => void;
  _setRedirectError: (msg: string | null) => void;
}

const useAuthStore = create<AuthState>((set) => ({
  user:          null,
  isLoading:     true,
  isVerified:    false,
  redirectError: null,

  _setUser: (user) =>
    set({ user, isVerified: user?.emailVerified ?? false }),
  _setLoading:       (isLoading)     => set({ isLoading }),
  _setRedirectError: (redirectError) => set({ redirectError }),
}));

// ─── HOOK ────────────────────────────────────────────────────────────────────
export function useAuth() {
  const {
    user, isLoading, isVerified, redirectError,
    _setUser, _setLoading, _setRedirectError,
  } = useAuthStore();

  useEffect(() => {
    // Handle result from signInWithRedirect (runs once on app boot after redirect)
    handleRedirectResult().then((res) => {
      if (res && !res.ok) {
        _setRedirectError((res as { message: string }).message);
      }
    });

    const unsubscribe = subscribeToAuthState((firebaseUser) => {
      _setUser(firebaseUser);
      _setLoading(false);
    });
    return unsubscribe;
  }, [_setUser, _setLoading, _setRedirectError]);

  return {
    user,
    isLoading,
    isVerified,
    redirectError,
    isSignedIn:  user !== null,
    uid:         user?.uid ?? null,
    displayName: user?.displayName ?? null,
    email:       user?.email ?? null,
    photoURL:    user?.photoURL ?? null,
  };
}

/**
 * Guard hook — redirects to sign-in if user is not authenticated.
 * Use in pages that require authentication.
 *
 * @example
 * function MyShelfPage() {
 *   useRequireAuth();
 *   return <div>...</div>;
 * }
 */
export function useRequireAuth(): { user: User; isVerified: boolean } {
  const { user, isLoading, isVerified } = useAuth();

  if (!isLoading && !user) {
    // In a real app, this would redirect to /signin.
    // Using a thrown error for now — caught by error boundaries.
    throw new Error("Authentication required");
  }

  return { user: user!, isVerified };
}
